import { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { DateRangeType } from "react-tailwindcss-datepicker";
import CustomDatePicker from "../awaitingInventory/CustomDatePicker";
import classNames from "../../utils/classNames";

interface DateRangeFilterOption {
  label: string;
  value: string;
}

interface DateRangeFilterProps {
  dates: DateRangeType;
  onDatesChange: (dates: DateRangeType) => void;
  dateFieldType: string;
  onDateFieldTypeChange: (type: string) => void;
  disabled?: boolean;
  className?: string;
  showLabels?: boolean;
  options?: DateRangeFilterOption[];
}

export default function DateRangeFilter({
  dates,
  onDatesChange,
  dateFieldType,
  onDateFieldTypeChange,
  disabled = false,
  className = "",
  showLabels = true,
  options = [
    { label: "Created", value: "Created" },
    { label: "Imaged On", value: "ImagedOn" },
    { label: "Listed On", value: "ListedDate" },
  ]
}: DateRangeFilterProps) {
  const { t } = useTranslation("common");
  const selectedOption = options.find(opt => opt.value === dateFieldType) || options[0];

  return (
    <div className={`flex flex-wrap gap-x-6 items-center ${className}`}>
      <div className="flex items-center">
        <div className="flex flex-col">
          {showLabels && <label className="text-sm font-medium text-gray-700 mb-1">{t("Date Field & Range")}</label>}
          <div className="relative flex items-center">
            <Menu as="div" className="relative">
              <Menu.Button
                disabled={disabled}
                className={classNames(
                  "flex h-10 items-center justify-center gap-x-1 rounded-l-md border border-r-0 border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                <span>{t(selectedOption.label)}</span>
                <ChevronDownIcon className="h-4 w-4" aria-hidden="true" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute left-0 mt-2 w-40 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                  <div className="py-1">
                    {options.map((opt) => (
                      <Menu.Item key={opt.value}>
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => onDateFieldTypeChange(opt.value)}
                            className={classNames(
                              active ? "bg-gray-100" : "",
                              dateFieldType === opt.value ? "bg-gray-50 font-medium" : "",
                              "w-full px-4 py-2 text-left text-sm text-gray-700"
                            )}
                          >
                            {t(opt.label)}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
            {/* @ts-ignore - CustomDatePicker is a .js file and accepts inputClassName prop */}
            <CustomDatePicker
              dates={dates}
              handleDatesChange={onDatesChange}
              inputClassName="h-10 text-sm border-gray-300 border-l-0 rounded-r-md focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 w-64"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

